import React, { useState } from 'react';
import { is } from 'ramda';
import { useTranslation } from 'react-i18next';
import { formatMoneyWithSign } from 'utils/currencyHelper';

import useScreen from 'hooks/useScreen';
import { Box, Typography } from '@material-ui/core';

import InputWithSlider from 'sharedComponents/InputWithSlider';
import PercentInputField from 'sharedComponents/PercentInputField';
import { affordabilityCalculatorInitialValues, affordabilityManuallyChangedFields } from 'utils/calculatorsHelper';
import MobileCalculationsResult from './components/MobileCalculationsResult';
import DesktopCalculationsResult from './components/DesktopCalculationsResult';
import useStyles from './useStyles';

const AffordabilityCalculator = () => {
  const { isDesktop, isMobile } = useScreen();
  const classes = useStyles();
  const { t } = useTranslation();
  const [calculatorValues, setCalculatorValues] = useState(affordabilityCalculatorInitialValues);
  const [manuallyChangedFields, setManuallyChangedFields] = useState(affordabilityManuallyChangedFields);

  const handleChangeIncomeValue = value => {
    if (!is(Number, value)) return;

    const carPaymentValue = manuallyChangedFields.carPaymentValue
      ? calculatorValues.carPaymentValue
      : Math.round((value / 12) * 0.04);
    const studentPaymentValue = manuallyChangedFields.studentPaymentValue
      ? calculatorValues.studentPaymentValue
      : Math.round((value / 12) * 0.01);
    const cardPaymentValue = manuallyChangedFields.cardPaymentManual
      ? calculatorValues.cardPaymentManual
      : Math.round((value / 12) * 0.01);

    setCalculatorValues({
      ...calculatorValues,
      carPaymentValue,
      studentPaymentValue,
      cardPaymentValue,
      incomeValue: value,
    });
  };

  const handleChangePaymentDetails = field => value => {
    if (!is(Number, value)) return;
    setCalculatorValues({ ...calculatorValues, [field]: value });
    setManuallyChangedFields({ ...manuallyChangedFields, [field]: true });
  };

  const handleChange = field => value => {
    if (!is(Number, value)) return;
    setCalculatorValues({ ...calculatorValues, [field]: value });
  };

  const handleChangeProcentValue = field => value => {
    if (!is(Number, value)) return;
    setCalculatorValues({ ...calculatorValues, [field]: value / 100 });
  };

  const {
    carPaymentValue,
    studentPaymentValue,
    cardPaymentValue,
    otherPaymentValue,
    incomeValue,
    debtToIncome,
    interestRate,
    downPaymentPercent,
    mortgageTerm,
    propertyTaxRate,
    mortgageInsurance,
  } = calculatorValues;

  const totalDebtPayments = carPaymentValue + studentPaymentValue + cardPaymentValue + otherPaymentValue;
  const mortgagePayment = (incomeValue * debtToIncome) / 12 - totalDebtPayments;
  const perDollarPandI =
    ((interestRate / 12) * (1 - downPaymentPercent)) / (1 - (1 + interestRate / 12) ** (mortgageTerm * -12));
  const perDollarTaxesAndInsurance = (propertyTaxRate + 0.004) / 12;
  const perDollarMortgageInsurance = (mortgageInsurance * (1 - downPaymentPercent)) / 12;
  const homePrice = mortgagePayment / (perDollarPandI + perDollarTaxesAndInsurance + perDollarMortgageInsurance);
  const downPayment =
    (mortgagePayment / (perDollarPandI + perDollarTaxesAndInsurance + perDollarMortgageInsurance)) * downPaymentPercent;

  const formattedDebtToIncome = `${Math.round(debtToIncome * 100)}%`;
  const formattedHomePrice = formatMoneyWithSign(Math.round(homePrice), 'USD');
  const formattedDownPayment = formatMoneyWithSign(Math.round(downPayment), 'USD');
  const formattedMortgagePayment = formatMoneyWithSign(Math.round(mortgagePayment), 'USD');
  const formattedTotalDebt = formatMoneyWithSign(Math.round(totalDebtPayments), 'USD');

  return (
    <Box className={classes.container}>
      {isMobile && (
        <MobileCalculationsResult
          homePrice={formattedHomePrice}
          downPayment={formattedDownPayment}
          mortgagePayment={formattedMortgagePayment}
          totalDebtPayments={formattedTotalDebt}
          debtToIncome={formattedDebtToIncome}
        />
      )}
      <Box className={classes.content}>
        {isDesktop && (
          <DesktopCalculationsResult
            homePrice={formattedHomePrice}
            downPayment={formattedDownPayment}
            mortgagePayment={formattedMortgagePayment}
            totalDebtPayments={formattedTotalDebt}
            debtToIncome={formattedDebtToIncome}
          />
        )}
        <Box>
          <Box mt={3}>
            <Typography className={classes.inputHeader} variant="body1">
              {t('components.AffordabilityCalculator.annualIncomeLabel')}
            </Typography>
            <InputWithSlider
              name="incomeValue"
              className={classes.input}
              value={calculatorValues.incomeValue}
              min={0}
              max={500000}
              marksDivider={1000}
              onChange={handleChangeIncomeValue}
              strictLimit
            />
          </Box>
          <Box mt={3}>
            <Typography className={classes.inputHeader} variant="body1">
              {t('components.AffordabilityCalculator.carPaymentLabel')}
            </Typography>
            <InputWithSlider
              name="carPaymentValue"
              className={classes.input}
              value={calculatorValues.carPaymentValue}
              min={0}
              max={2500}
              marksDivider={1}
              onChange={handleChangePaymentDetails('carPaymentValue')}
              strictLimit
            />
          </Box>
          <Box mt={3}>
            <Typography className={classes.inputHeader} variant="body1">
              {t('components.AffordabilityCalculator.studentPaymentLabel')}
            </Typography>
            <InputWithSlider
              name="studentPaymentValue"
              className={classes.input}
              value={calculatorValues.studentPaymentValue}
              min={0}
              max={2000}
              marksDivider={1}
              onChange={handleChangePaymentDetails('studentPaymentValue')}
              strictLimit
            />
          </Box>
          <Box mt={3}>
            <Typography className={classes.inputHeader} variant="body1">
              {t('components.AffordabilityCalculator.cardPaymentLabel')}
            </Typography>
            <InputWithSlider
              name="cardPaymentValue"
              className={classes.input}
              value={calculatorValues.cardPaymentValue}
              min={0}
              max={2000}
              marksDivider={1}
              onChange={handleChangePaymentDetails('cardPaymentValue')}
              strictLimit
            />
          </Box>
          <Box mt={3}>
            <Typography className={classes.inputHeader} variant="body1">
              {t('components.AffordabilityCalculator.otherPaymentLabel')}
            </Typography>
            <InputWithSlider
              name="otherPaymentValue"
              className={classes.input}
              value={calculatorValues.otherPaymentValue}
              min={0}
              max={2000}
              marksDivider={1}
              onChange={handleChange('otherPaymentValue')}
              strictLimit
            />
          </Box>
        </Box>
        <Box mt={4}>
          <Typography variant="h1">{t('components.AffordabilityCalculator.mortgageDetails')}</Typography>
          <Box mt={3}>
            <Typography className={classes.inputHeader} variant="body1">
              {t('components.AffordabilityCalculator.debtToIncomeLabel')}
            </Typography>
            <InputWithSlider
              name="debtToIncome"
              className={classes.input}
              value={calculatorValues.debtToIncome * 100}
              min={0}
              max={50}
              suffix="%"
              marksDivider={1}
              onChange={handleChangeProcentValue('debtToIncome')}
              strictLimit
            />
          </Box>
          <Box mt={3}>
            <Typography className={classes.inputHeader} variant="body1">
              {t('components.AffordabilityCalculator.downPaymentPercentLabel')}
            </Typography>
            <InputWithSlider
              name="downPaymentPercent"
              className={classes.input}
              value={calculatorValues.downPaymentPercent * 100}
              min={0}
              max={50}
              suffix="%"
              marksDivider={1}
              onChange={handleChangeProcentValue('downPaymentPercent')}
              strictLimit
            />
          </Box>
          <Box mt={3}>
            <Typography className={classes.inputHeader} variant="body1">
              {t('components.AffordabilityCalculator.mortgageTermLabel')}
            </Typography>
            <InputWithSlider
              name="mortgageTerm"
              className={classes.input}
              value={calculatorValues.mortgageTerm}
              min={10}
              max={30}
              prefix=""
              marksDivider={1}
              onChange={handleChange('mortgageTerm')}
              strictLimit
            />
          </Box>
          <Box mt={3}>
            <Typography className={classes.inputHeader} variant="body1">
              {t('components.AffordabilityCalculator.mortgageInterestRateLabel')}
            </Typography>
            <PercentInputField
              value={calculatorValues.interestRate * 100}
              min={0}
              max={7}
              stepDiff={0.125}
              onChange={handleChangeProcentValue('interestRate')}
            />
          </Box>
          <Box mt={3}>
            <Typography className={classes.inputHeader} variant="body1">
              {t('components.AffordabilityCalculator.propertyTaxRateLabel')}
            </Typography>
            <PercentInputField
              value={calculatorValues.propertyTaxRate * 100}
              min={0}
              max={3}
              stepDiff={0.1}
              onChange={handleChangeProcentValue('propertyTaxRate')}
            />
          </Box>
          <Box mt={3} mb={2}>
            <Typography className={classes.inputHeader} variant="body1">
              {t('components.AffordabilityCalculator.mortgageInsuranceLabel')}
            </Typography>
            <PercentInputField
              value={calculatorValues.mortgageInsurance * 100}
              min={0}
              max={3}
              stepDiff={0.05}
              onChange={handleChangeProcentValue('mortgageInsurance')}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AffordabilityCalculator;



// WEBPACK FOOTER //
// ./src/sharedComponents/AffordabilityCalculator/AffordabilityCalculator.js